import { useState, type ReactNode } from 'react';
import { Icon, type IconName } from '../ui/Icon';
import { Badge, Button, Card } from '../ui/primitives';
import type { Tone } from '../ui/primitives/cx';
import type { MySubmissionItem, MySubmissions } from '../../types/dashboard';
import { CardHead, InlineError } from './InicioParts';

const TITLE_ID = 'inicio-submissions-title';

// Lado del empleado en el Inicio: el estado de lo que ÉL envió (propuestas de catálogo, solicitudes
// y compras). Solo lectura. Rechazos y anulaciones siempre a la vista (traen motivo, son lo
// accionable); pendientes a la vista; aprobadas plegadas (informativas, crecerían mucho).
const kindIcon = (kind: MySubmissionItem['kind']): IconName => (kind === 'VOID_REQUEST' ? 'void' : kind === 'INGREDIENT' ? 'inventory' : kind === 'EXPENSE' ? 'expenses' : 'purchases');
// VOID_REQUEST → «Solicitud»: el nombre ya trae el tipo real («Anulación · …» / «Modificación · …»).
const kindLabel = (kind: MySubmissionItem['kind']) => (kind === 'VOID_REQUEST' ? 'Solicitud' : kind === 'INGREDIENT' ? 'Insumo' : kind === 'PURCHASE' ? 'Compra' : kind === 'EXPENSE' ? 'Gasto' : 'Proveedor');

type RowTone = 'danger' | 'info' | 'success' | 'muted';
const BADGE_TONE: Record<RowTone, Tone | 'neutral'> = { danger: 'danger', info: 'info', success: 'success', muted: 'neutral' };

function Row({ item, tone, chip, note }: { item: MySubmissionItem; tone: RowTone; chip: string; note: string }) {
  const icon: IconName = tone === 'danger' || tone === 'muted' ? 'close' : tone === 'success' ? 'success' : kindIcon(item.kind);
  return (
    <li className="n3-inicio__submission">
      <span aria-hidden="true" className={`n3-inicio__tile n3-inicio__tile--${tone === 'muted' ? 'neutral' : tone}`}><Icon name={icon} size={18} /></span>
      <div className="n3-inicio__item-body">
        <p className="n3-inicio__item-title">{kindLabel(item.kind)}: {item.name}</p>
        <p className="n3-inicio__item-detail">{note}{item.reason ? ` · ${item.reason}` : ''}</p>
      </div>
      <Badge tone={BADGE_TONE[tone]}>{chip}</Badge>
    </li>
  );
}

function Section({ label, count, defaultOpen, children }: { label: string; count: number; defaultOpen: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!count) return null;
  return (
    <div className="n3-inicio__submissions-section">
      <Button aria-expanded={open} className="n3-inicio__link" onClick={() => setOpen(!open)} variant="ghost">
        {label} ({count}) · {open ? 'ocultar' : 'mostrar'}
      </Button>
      {open && <ul className="n3-inicio__submissions">{children}</ul>}
    </div>
  );
}

/** ¿Hay algo que mostrar? La página lo pregunta para no reservarle sitio a una tarjeta vacía. */
export function hasSubmissions(data: MySubmissions | null): boolean {
  if (!data) return false;
  return data.pending.length > 0 || data.rejected.length > 0 || (data.approved ?? []).length > 0 || (data.voided ?? []).length > 0;
}

interface InicioSubmissionsProps {
  data: MySubmissions | null;
  error: boolean;
  onRetry: () => void;
}

/**
 * «El estado de lo que enviaste» — solo para el empleado, como antes: lo mismo, con la piel nueva.
 * Si no ha enviado nada, la tarjeta no aparece. Si la consulta falla, ahora se dice.
 */
export function InicioSubmissions({ data, error, onRetry }: InicioSubmissionsProps) {
  if (error) {
    return (
      <Card aria-labelledby={TITLE_ID} as="section">
        <CardHead id={TITLE_ID} title="El estado de lo que enviaste" />
        <InlineError onRetry={onRetry} text="No pude traer el estado de lo que enviaste." />
      </Card>
    );
  }
  if (!data || !hasSubmissions(data)) return null;
  const { pending, rejected, approved = [], voided = [] } = data;

  return (
    <Card aria-labelledby={TITLE_ID} as="section">
      <CardHead id={TITLE_ID} title="El estado de lo que enviaste" />
      <p className="n3-inicio__support">Tus propuestas, solicitudes y compras</p>
      {pending.length > 0 && (
        <ul className="n3-inicio__submissions">
          {pending.map((item) => <Row chip="Pendiente" item={item} key={`p-${item.id}`} note="En espera de aprobación" tone="info" />)}
        </ul>
      )}
      <Section count={rejected.length} defaultOpen label="Rechazadas">
        {rejected.map((item) => <Row chip="Rechazado" item={item} key={`r-${item.id}`} note="Rechazado" tone="danger" />)}
      </Section>
      <Section count={voided.length} defaultOpen label="Anulados después de registrarse">
        {voided.map((item) => (
          <Row
            chip={item.kind === 'EXPENSE' ? 'Anulado' : 'Anulada'}
            item={item}
            key={`v-${item.id}`}
            note={item.kind === 'EXPENSE' ? 'Un dueño o gerente lo anuló; el corte de ese turno no cambia' : 'Se aprobó y después se anuló'}
            tone="muted"
          />
        ))}
      </Section>
      <Section count={approved.length} defaultOpen={false} label="Aprobadas recientemente">
        {approved.map((item) => <Row chip="Aprobada" item={item} key={`a-${item.id}`} note="Aprobada" tone="success" />)}
      </Section>
    </Card>
  );
}
